import React, { useMemo, useState } from 'react';
import { DataGrid, GridColDef, GridCellEditStopParams } from '@mui/x-data-grid';
import { Paper, Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';

interface SegmentationTableProps {
    data: any[];
    onChange: (newData: any[]) => void;
    title?: string; // Optional title for CSV download filename
}

const StyledDataGrid = styled(DataGrid)({
    border: 'none',
    '& .MuiDataGrid-cell': {
        padding: '0px 8px',
        border: '1px solid #e0e0e0',
    },
    '& .MuiDataGrid-columnHeader': {
        backgroundColor: '#f5f5f5',
        fontWeight: 'bold',
        border: '1px solid #e0e0e0',
    },
    '& .MuiDataGrid-cell:focus': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
    }
});

export const SegmentationTable: React.FC<SegmentationTableProps> = ({ data, onChange, title = 'segmentation-data' }) => {
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [newColumnDialog, setNewColumnDialog] = useState(false);
    const [newColumnName, setNewColumnName] = useState('');

    const rows = useMemo(() => 
        data.map((row, index) => ({
            id: index,
            ...row
        }))
    , [data]);

    const columns: GridColDef[] = useMemo(() => 
        data.length ? Object.keys(data[0]).map(key => ({
            field: key,
            headerName: key,
            flex: 1,
            editable: true,
        })) : []
    , [data]);

    const handleEditStop = (params: GridCellEditStopParams) => {
        const { id, field, value } = params;
        const newData = [...data];
        newData[id as number] = {
            ...newData[id as number],
            [field]: value
        };
        onChange(newData);
    };

    const handleAddRow = () => {
        const newRow = data.length > 0 
            ? Object.keys(data[0]).reduce((acc, key) => ({...acc, [key]: ''}), {})
            : { newColumn: '' };
        
        const newData = [...data, newRow];
        onChange(newData);
    };

    const handleDeleteSelected = () => {
        if (selectedRows.length === 0) return;
        
        const newData = data.filter((_, index) => !selectedRows.includes(index));
        onChange(newData);
        setSelectedRows([]);
    };

    // Function to download table data as CSV
    const downloadCSV = () => {
        if (data.length === 0 || columns.length === 0) return;
        
        // Create CSV header row
        const headers = columns.map(col => `"${col.headerName || col.field}"`).join(',');
        
        // Create CSV data rows
        const csvRows = data.map(row => {
            return columns.map(col => {
                // Get the value for this column
                let value = row[col.field];
                
                // Handle objects and arrays by converting to JSON string
                if (typeof value === 'object' && value !== null) {
                    value = JSON.stringify(value);
                }
                
                // Escape quotes and wrap in quotes
                if (value === null || value === undefined) {
                    return '""';
                }
                return `"${String(value).replace(/"/g, '""')}"`;
            }).join(',');
        }).join('\n');
        
        // Combine header and rows
        const csvContent = `${headers}\n${csvRows}`;
        
        // Create a blob and download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${title.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleAddColumn = () => {
        if (!newColumnName.trim()) return;
        
        const newData = data.map(row => ({
            ...row,
            [newColumnName]: ''
        }));
        
        onChange(newData);
        setNewColumnDialog(false);
        setNewColumnName('');
    };

    const handleDeleteColumn = (columnName: string) => {
        const newData = data.map(row => {
            const newRow = {...row};
            delete newRow[columnName];
            return newRow;
        });
        
        onChange(newData);
    };

    if (!data.length && columns.length === 0) {
        return (
            <Paper 
                sx={{ 
                    height: 400,
                    width: '100%',
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column'
                }}
            >
                <p>No data available. Add a row to begin.</p>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />} 
                    onClick={handleAddRow}
                >
                    Add Row
                </Button>
            </Paper>
        );
    }

    return (
        <Paper elevation={0} sx={{ p: 2, width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Tooltip title="Download as CSV">
                    <IconButton 
                        onClick={downloadCSV}
                        size="small"
                        sx={{ mr: 1 }}
                        color="primary"
                    >
                        <DownloadIcon />
                    </IconButton>
                </Tooltip>
                
                <Button 
                    startIcon={<AddIcon />} 
                    onClick={handleAddRow}
                    variant="outlined"
                    size="small"
                    sx={{ mr: 1 }}
                >
                    Add Row
                </Button>
                <Button 
                    startIcon={<DeleteIcon />} 
                    onClick={handleDeleteSelected}
                    variant="outlined"
                    size="small"
                    disabled={selectedRows.length === 0}
                    color="error"
                >
                    Delete Selected
                </Button>
            </Box>
            <Paper 
                sx={{ 
                    height: 400,
                    width: '100%',
                    border: '1px solid #e0e0e0',
                }}
            >
                <StyledDataGrid
                    rows={rows}
                    columns={columns}
                    disableRowSelectionOnClick
                    onCellEditStop={handleEditStop}
                    disableColumnMenu
                    hideFooter={data.length <= 100}
                    checkboxSelection
                    onRowSelectionModelChange={(newSelection) => {
                        setSelectedRows(newSelection as number[]);
                    }}
                />
            </Paper>

            <Dialog open={newColumnDialog} onClose={() => setNewColumnDialog(false)}>
                <DialogTitle>Add New Column</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Column Name"
                        fullWidth
                        variant="outlined"
                        value={newColumnName}
                        onChange={(e) => setNewColumnName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewColumnDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddColumn}>Add</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};
